import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { Toaster } from "sonner";

import { BloqueioTrocaSenha } from "./components/auth/BloqueioTrocaSenha";
import { api, registrarRedirecionadores, renovarToken } from "./lib/api";
import { router } from "./rotas";
import type { ContextoEuResposta } from "@zaphub/types";
import { contextoParaSessao, useSessao } from "./lib/sessao";
import { useTema } from "./lib/tema";
import "./styles.css";

const qc = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: false },
  },
});

registrarRedirecionadores({
  perderSessao: () => void router.navigate("/entrar", { replace: true }),
  assinaturaInativa: () => void router.navigate("/cobranca/assinatura-necessaria", { replace: true }),
});

/** Tenta restaurar a sessão pelo cookie de refresh antes de renderizar as rotas. */
async function restaurarSessao(): Promise<void> {
  const { definirSessao, marcarIniciada } = useSessao.getState();
  try {
    const token = await renovarToken();
    if (token) {
      const { data } = await api.get<ContextoEuResposta>("/auth/eu");
      definirSessao(contextoParaSessao(data));
    }
  } catch {
    useSessao.getState().limpar();
  } finally {
    marcarIniciada();
  }
}

function App() {
  const tema = useTema((s) => s.tema);
  useEffect(() => {
    void restaurarSessao();
  }, []);
  return (
    <QueryClientProvider client={qc}>
      <RouterProvider router={router} />
      <BloqueioTrocaSenha aoSair={() => void router.navigate("/entrar", { replace: true })} />
      <Toaster theme={tema === "escuro" ? "dark" : "light"} position="top-right" richColors closeButton />
    </QueryClientProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
